/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, Suspense, useRef } from "react";
import { View, Text, StyleSheet, LogBox, Platform, Dimensions } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import DLOHeader from "./src/modules/dlo/components/DLOHeader";
import DLOSidebar from "./src/modules/dlo/components/DLOSidebar";
import type { PerfectTruckScreenHandle } from "./src/screens/PerfectTruck";
import sidebarItems from "./src/components/LHSidebarItems";
import LocationInitializer from "./src/components/LocationInitializer";
import AnnouncementBanner from "./src/screens/homescreen/components/AnnouncementBanner";
// Remove wrapper imports - use direct lazy loading instead
import { Provider } from "react-redux";
import store from "./src/store";
import { ThemeProvider } from "./src/theme";
import { MsalProvider } from "./src/components/auth/MsalProvider";
import AppWrapper from "./src/components/AppWrapper";
import { LhToastProvider, useToast } from "./src/components/LhToastContainer";
import LHLoader from "./src/components/LHLoader";
import DLOApp from "./src/modules/dlo/DLOApp";
import { EnhancedErrorBoundary } from "./src/components/EnhancedErrorBoundary";
import initializeGlobalApiInterceptor from "./src/utils/GlobalApiInterceptor";
import { initializeUserContext, initializeStoreListener } from "./src/utils/Instrumentation";
import memoryMonitor from "./src/utils/memoryMonitor";
import { logger } from "./src/utils/logger";
import { smartPreloader } from "./src/utils/SmartPreloader";
import FullStory from "./src/utils/fullStory";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "./src/store";
import { setTicketDetailSource, setSelectedTicketId } from "./src/store/slices/ticketingPortalSlice";
import { restoreLastScreen, saveScreen } from "./src/hooks/useNavigationPersistence";
import { resolveRestoreScreen } from "./src/config/navigationConfig";

// Lazy-loaded screen components with direct imports
const HomeScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.home" */
  "./src/screens/homescreen/HomeScreen"
));

const ObservationScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.observation" */
  "./src/screens/Observation"
));

const ShiftHandoverScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.shift-handover" */
  "./src/screens/ShiftHandover"
));

const ShiftHandoverRedirectScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.shift-handover-redirect" */
  "./src/screens/ShiftHandoverRedirectScreen"
));

const LeaderShipWalkScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.leadership-walk" */
  "./src/screens/LeaderShipWalkScreen"
));

const LeaderShipWalkRedirectScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.leadership-walk-redirect" */
  "./src/screens/LeaderShipWalkRedirectScreen"
));

const SparcWalkScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.sparc-walk" */
  "./src/screens/SparcWalkScreen"
));

const RackingInspectionScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.racking-inspection" */
  "./src/screens/racking_inspection/RackingInspectionScreen"
));

const ContactUsScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.contact-us" */
  "./src/screens/ContactUs/ContactUsScreen"
));

const QualityAuditScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.quality-audit" */
  "./src/screens/QualityAudit"
));

const SiteEvaluationScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.site-evaluation" */
  "./src/screens/siteEvaluation/SiteEvaluation"
));

const AnalyticsScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.analytics" */
  "./src/screens/AnalyticsHub/pages/AnalyticsHubDashBoard"
));

const TicketingAnalyticsReportScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.analytics.ticketing" */
  "./src/screens/NavigatorReports/pages/TicketingAnalytics/TicketingAnalyticsDashboard"
));

// ─── Internal Redirect Reports (Navigator) ─────────────────────────────────────
// These screens are rendered when an INTERNAL_REDIRECT report is opened from Analytics Hub.
// The report's `link` field is used directly as the screen name in navigateTo().
// To add a new internal report:
//   1. Configure it in the backend with reportType = INTERNAL_REDIRECT and a unique link value
//   2. Create the screen component
//   3. Add a matching case in renderScreen() below
const PerfectTruckAnalyticsDashboard = React.lazy(() => import(
  /* webpackChunkName: "screen.navigator-reports.perfect-truck-analytics" */
  "./src/screens/NavigatorReports/pages/PerfectTruckAnalytics/PerfectTruckAnalyticsDashboard"
));

const CrewingAnalyticsDashboard = React.lazy(() => import(
  /* webpackChunkName: "screen.navigator-reports.crewing-analytics" */
  "./src/screens/NavigatorReports/pages/CrewingAnalytics/CrewingAnalyticsDashboard"
));

const OrderRulesReport = React.lazy(() => import(
  /* webpackChunkName: "screen.navigator-reports.order-rules" */
  "./src/screens/NavigatorReports/pages/OrderRulesAnalytics/OrderRulesReport"
));

const OnTimeDispatchDashboard = React.lazy(() => import(
  /* webpackChunkName: "screen.navigator-reports.on-time-dispatch" */
  "./src/screens/NavigatorReports/pages/OnTimeDispatch/OnTimeDispatchDashboard"
));

const AsnGapDashboard = React.lazy(() => import(
  /* webpackChunkName: "screen.navigator-reports.asn-gap" */
  "./src/screens/NavigatorReports/pages/AsnGap/AsnGapDashboard"
));

const WesPalletStagingAnalyticsDashboard = React.lazy(() => import(
  /* webpackChunkName: "screen.navigator-reports.wes-pallet-staging-analytics" */
  "./src/screens/NavigatorReports/pages/WesPalletStagingAnalytics/WesPalletStagingAnalyticsDashboard"
));

const NewHireOnboardingScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.new-hire-onboarding" */
  "./src/screens/NewHireOnboarding"
));

const PeopleHubScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.people-hub" */
  "./src/screens/PeopleHub"
));

const PeopleHubActivityScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.people-hub-activity" */
  "./src/screens/PeopleHub/BeginActivity/PulseCheck"
));

const PeopleHubObservationScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.people-hub-observation" */
  "./src/screens/PeopleHub/BeginActivity/RoutineObservation"
));

const ManageEmployeeUpdatesScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.manage-employees" */
  "./src/screens/NewHireOnboarding/pages/ManageEmployeeUpdates"
));

const PulseCheck = React.lazy(() => import(
  /* webpackChunkName: "component.pulse-check" */
  "./src/screens/PulseCheck/index"
));

const PulseCheckTouchPointScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.pulse-check-touchpoint" */
  "./src/screens/NewHireOnboarding/pages/touchpoints/PulseCheckTouchPoint"
));

const SpecificPeriodTouchPointScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.specific-period-touchpoint" */
  "./src/screens/NewHireOnboarding/pages/touchpoints/SpecificPeriodTouchPoint"
));

const ActionPlanScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.action-plan" */
  "./src/screens/ActionPlan"
));

const AdminDashboardScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.admin-dashboard" */
  "./src/screens/Admin/pages/AdminDashboard"
));

const PerfectTruckScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.perfect-truck" */
  "./src/screens/PerfectTruck"
));

const HarborLandingScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.harbor" */
  "./src/screens/harbor/HarborLanding"
));

const DeliveryRPMScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.delivery-rpm" */
  "./src/screens/harbor/DeliveryRPMScreen"
));

const StandUpScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.stand-up" */
  "./src/screens/StandUp/pages/StandUpScreen"
));

const TicketingPortalScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.ticketing-portal" */
  "./src/screens/TicketingPortal/TicketingPortalScreen"
));

const SubmitTicketScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.submit-ticket" */
  "./src/screens/TicketingPortal/SubmitTicketScreen"
));

const MyTicketsScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.my-tickets" */
  "./src/screens/TicketingPortal/MyTicketsScreen"
));

const SustainQueueScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.sustain-queue" */
  "./src/screens/TicketingPortal/SustainQueueScreen"
));

const TicketDetailScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.ticket-detail" */
  "./src/screens/TicketingPortal/TicketDetailScreen"
));

const TicketPackingSlipScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.ticket-packing-slip" */
  "./src/screens/TicketingPortal/TicketPackingSlipScreen"
));

const NotificationsCommsScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.notifications-comms" */
  "./src/screens/TicketingPortal/NotificationsCommsScreen"
));

const SustainTeamManagementScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.sustain-team-management" */
  "./src/screens/TicketingPortal/SustainTeamManagementScreen"
));

const TicketingConfigurationsScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.ticketing-configurations" */
  "./src/screens/TicketingPortal/TicketingConfigurationsScreen"
));

const SparePoolScreen = React.lazy(() => import(
  /* webpackChunkName: "screen.spare-pool" */
  "./src/screens/TicketingPortal/SparePoolScreen"
));

// Initialize global error logging and user context
initializeGlobalApiInterceptor();

// Initialize AppDynamics user context and store listener
initializeStoreListener();
initializeUserContext();

// Initialize memory monitoring for web platform
if (Platform.OS === 'web') {
  memoryMonitor.startMonitoring();
}

// Suppress react-native-render-html defaultProps deprecation warnings
LogBox.ignoreLogs([
  "Warning: TRenderEngineProvider: Support for defaultProps will be removed",
  "Warning: MemoizedTNodeRenderer: Support for defaultProps will be removed", 
  "Warning: TNodeChildrenRenderer: Support for defaultProps will be removed"
]);

// Maps a saved screen to the module's root/dashboard on reload.
// Detail/redirect screens depend on transient state that is lost on hard refresh,
// so we always restore the module entry point instead.
// ✓ Moved to: src/config/navigationConfig.ts

// Screens that can be restored from persistent storage.
// ✓ Moved to: src/config/navigationConfig.ts

// Resolves a raw saved screen string to a valid screen with params.
// ✓ Moved to: src/config/navigationConfig.ts

const App = (props?: { initialRoute?: string; ticketId?: string; notificationId?: string }) => {
  const [activeTab, setActiveTab] = useState("Home");
  const [screenStack, setScreenStack] = useState(["Home"]);
  const [isNavigationHydrated, setIsNavigationHydrated] = useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  const [isTablet, setIsTablet] = React.useState(false);
  const [actionPlanNavCounter, setActionPlanNavCounter] = useState(0);
  const [standUpNavCounter, setStandUpNavCounter] = useState(0);
  const [adminDashboardNavCounter, setAdminDashboardNavCounter] = useState(0);
  const [perfectTruckNavCounter, setPerfectTruckNavCounter] = useState(0);
  const [screenParams, setScreenParams] = useState<{ [key: string]: any }>({});
  const perfectTruckScreenRef = useRef<PerfectTruckScreenHandle>(null);

  // Restore last active screen on app load (all platforms).
  // Runs once on mount; sets hydrated flag so the app never renders before
  // the correct screen is known.
  React.useEffect(() => {
    let active = true;
    
    // Handle deep link props (e.g., from URL like /ticket-details/ticket-id/NTI-2026-02598)
    if (props?.initialRoute && props?.ticketId && active) {
      setActiveTab(props.initialRoute);
      setScreenStack([props.initialRoute]);
      // Mark as deep-linked so access control allows viewing
      setScreenParams({ ticketId: props.ticketId, isDeepLinked: true });
      saveScreen(props.initialRoute, { ticketId: props.ticketId, isDeepLinked: true });
      setIsNavigationHydrated(true);
      return;
    }

    // Handle deep link props for a notification (e.g., Teams "View Details" button)
    if (props?.initialRoute && props?.notificationId && active) {
      setActiveTab(props.initialRoute);
      setScreenStack([props.initialRoute]);
      setScreenParams({ notificationId: props.notificationId, isDeepLinked: true });
      saveScreen(props.initialRoute, { notificationId: props.notificationId, isDeepLinked: true });
      setIsNavigationHydrated(true);
      return;
    }

    restoreLastScreen().then((saved) => {
      if (!active) return;
      if (saved) {
        const resolved = resolveRestoreScreen(saved);
        if (resolved) {
          setActiveTab(resolved.screen);
          setScreenStack([resolved.screen]);
          // Restore params if they exist (e.g., ticketId for Ticket Detail)
          if (Object.keys(resolved.params).length > 0) {
            setScreenParams(resolved.params as any);
          }
          // Re-sync storage so URL hash stays consistent with what we actually show.
          saveScreen(resolved.screen, resolved.params);
        }
      }
      setIsNavigationHydrated(true);
    });
    return () => { active = false; };
  }, []);

  interface NavigateToFunction {
    (screen: string, params?: { [key: string]: any }): void;
  }

  const navigateTo: NavigateToFunction = (screen, params) => {
    setScreenStack([...screenStack, screen]);
    setActiveTab(screen);
    
    if (screen === 'Action Plan') {
      setActionPlanNavCounter(prev => prev + 1);
    }
    
    if (screen === 'Stand Up') {
      setStandUpNavCounter(prev => prev + 1);
    }

    if (screen === 'Admin Dashboard') {
      setAdminDashboardNavCounter(prev => prev + 1);
    }

    if (screen === 'Perfect Truck') {
      setPerfectTruckNavCounter(prev => prev + 1);
    }
    
    // Set screen parameters if provided
    if (params) {
      setScreenParams(params);
    } else {
      setScreenParams({});
    }
    
    // Persist current screen + params (all platforms: AsyncStorage + URL hash on web)
    saveScreen(screen, params);

    // Trigger smart preloading for related screens
    smartPreloader.preloadForScreen(screen, 1500);
  }; // Specialized navigation functions for NHO module

  // Expose a global navigation helper so deep components can request navigation
  // without threading the navigate function through many props.
  // Use `globalThis.appNavigate(screen, params)` to navigate.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (globalThis as any).appNavigate = navigateTo;
  } catch (e) {
    // ignore failures assigning to global
  }
  const navigateToNHOAdmin = () => {
    navigateTo("NHO Admin");
  };
 
  const navigateToNHOManageEmployees = () => {
    navigateTo("NHO Manage Employees");
  };

  const navigateToNHOBegin = (
    destination?: string,
    filters?: { employee: string; observationType: string }
  ) => {
    if (
      destination === "PulseCheckTouchPoint" &&
      filters?.observationType === "pulse_check"
    ) {
      navigateTo("NHO PulseCheckTouchPoint");
    } else if (
      destination === "SpecificPeriodTouchPoint" &&
      ["30", "60", "90", "preferred_method"].includes(
        filters?.observationType || ""
      )
    ) {
      navigateTo("NHO SpecificPeriodTouchPoint");
    } else {
      // Default navigation if no specific destination or invalid combination
      navigateTo("NHO Begin");
    }
  };
 
  // Track selected employee ID for the profile view
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<
    string | undefined
  >();

  const navigateToNHOEmployeeProfile = (employeeId: string) => {
    logger.log(
      `Navigating to NHO Employee Profile for ID in apps: ${employeeId}`
    );
    // Set the employee ID first
    setSelectedEmployeeId(employeeId);
    // Use setTimeout to ensure state is updated before navigation
    setTimeout(() => {
      navigateTo("NHOEmployeeProfile");
    }, 0);
  };
 
  const goBack = () => {
    if (screenStack.length > 1) {
      const newStack = [...screenStack];
      newStack.pop();
      setScreenStack(newStack);
      setActiveTab(newStack[newStack.length - 1]);
      // Clear screen parameters when going back
      setScreenParams({});
    }
  };
 
  // Navigate back to Dashboard specifically for touchpoint screens
  const goBackToDashboard = () => {
    setScreenStack(["NHO"]);
    setActiveTab("NHO");
  };
 
  // peopleHubActiveSession is selected inside AppContentWrapper (under <Provider>)
  // and passed in here — People Hub activity/observation session, stored in redux
  // (not route params/URL) by PeopleHub/index.tsx when the user clicks Proceed.
  const renderScreen = (peopleHubActiveSession?: any) => {
    const currentScreen = screenStack[screenStack.length - 1];

    // Wrap all screen rendering in Suspense for both web and native
    return (
      <Suspense fallback={<LHLoader />}>
        {(() => {
          switch (currentScreen) {
            case "Home":
              return <HomeScreen />;
            case "Observations":
              return (
                <ObservationScreen
                  onEmployeeUpdateClick={() => navigateTo("EmployeeUpdate")}
                  onPulseCheckScreenClick={() => navigateTo("PulseCheck")}
                />
              );
            case "Shift Handover":
              return <ShiftHandoverScreen navigateTo={navigateTo} />;
            case "Employee Update":
              return <ManageEmployeeUpdatesScreen onBackPress={goBack} />;
            case "EmployeeUpdate":
              return <ManageEmployeeUpdatesScreen onBackPress={goBack} />;
            case "PulseCheck":
              return <PulseCheck onBackPress={goBack} onHomePress={() => navigateTo("Home")} />;
            case "Leadership Walk":
              return (
                <LeaderShipWalkScreen
                  onBackPress={goBack}
                  onLeadershipWalkRedirectClick={() =>
                    navigateTo("LeadershipWalkRedirect")
                  }
                  onSiteEvaluationClick={() => navigateTo("Site Evaluation")}
                />
              );
            case "SPARC Walk":
              return <SparcWalkScreen onBackPress={goBack} />;
            case "People Hub":
              return (
                <PeopleHubScreen
                  onBackPress={() => navigateTo("Home")}
                  onBeginActivity={() => navigateTo("PeopleHub Activity")}
                  onBeginObservation={() => navigateTo("PeopleHub Observation")}
                />
              );
            case "PeopleHub Activity":
              return (
                <PeopleHubActivityScreen
                  session={peopleHubActiveSession}
                  onExit={goBack}
                  onComplete={goBack}
                />
              );
            case "PeopleHub Observation":
              return (
                <PeopleHubObservationScreen
                  session={peopleHubActiveSession}
                  onExit={goBack}
                  onComplete={goBack}
                />
              );
            case "ShiftHandoverRedirect":
              return <ShiftHandoverRedirectScreen onBackPress={goBack} />;
            case "Racking Inspection":
              return <RackingInspectionScreen onBackPress={goBack} onHomePress={() => navigateTo("Home")} />;
            case "LeadershipWalkRedirect":
              return (
                <LeaderShipWalkRedirectScreen
                  onBackPress={goBack}
                  onHomePress={() => navigateTo("Home")}
                />
              );
            case "Contact us/Feedback":
              return <ContactUsScreen onBackPress={goBack} />;
            case "Quality Audit":
              return <QualityAuditScreen onBackPress={goBack} />;
            case "Admin Dashboard":
              return <AdminDashboardScreen key={`AdminDashboard-${adminDashboardNavCounter}`} onBackPress={goBack} />;
            case "Perfect Truck":
              return (
                <PerfectTruckScreen
                  ref={perfectTruckScreenRef}
                  key={`PerfectTruck-${perfectTruckNavCounter}`}
                  onBackPress={screenParams?.fromHarbor ? () => navigateTo("Harbor") : () => navigateTo("Home")}
                />
              );
            case "Harbor":
              return <HarborLandingScreen onNavigate={navigateTo} />;
            case "Delivery RPM":
              return <DeliveryRPMScreen onNavigate={navigateTo} />;
            case "Site Evaluation":
              return (
                <SiteEvaluationScreen
                  onBackPress={goBack}
                  onHomePress={() => navigateTo("Home")}
                  siteEvalClick={() => {/* Add navigation or logic here if needed */}}
                  activeItemsClick={() => {/* Add navigation or logic here if needed */}}
                  onActionPlanPress={() => navigateTo("Action Plan")}
                />
              );
            case "Analytics":
              return (
                <AnalyticsScreen
                  initialTab={screenParams?.tab === 'all-reports' ? 'all-reports' : undefined}
                  onOpenReportPage={(reportId: string) => {
                    // Internal redirect reports pass their link as the identifier
                    // (e.g., "/navigator-reports/perfect-truck-analytics")
                    if (reportId.startsWith("/navigator-reports/")) {
                      navigateTo(reportId);
                      return;
                    }
                  }}
                />
              );
            case "Ticketing Analytics Report":
              return <TicketingAnalyticsReportScreen onBack={() => navigateTo("Ticketing Portal")} />;
            // ─── Internal Redirect Report Screens ───────────────────────────────
            // Each INTERNAL_REDIRECT report's `link` value maps to a case here.
            // To add a new internal report screen:
            //   1. Add backend config with reportType = INTERNAL_REDIRECT, link = "/navigator-reports/<slug>"
            //   2. Create the screen component
            //   3. Add a case below matching the link value
            // Example: case "/navigator-reports/driver-productivity":
            //            return <DriverProductivityDashboard />;
            case "/navigator-reports/perfect-truck-analytics":
              return <PerfectTruckAnalyticsDashboard />;
            case "/navigator-reports/order-rules":
              return <OrderRulesReport />;
            case "/navigator-reports/crewing-analytics":
              return <CrewingAnalyticsDashboard />;
            case "/navigator-reports/asn-gap":
              return <AsnGapDashboard />;
            case "/navigator-reports/on-time-dispatch":
              return <OnTimeDispatchDashboard />;
            case "/navigator-reports/wes-pallet-staging-analytics":
              return <WesPalletStagingAnalyticsDashboard />;
            case "Action Plan":
              return (
                <ActionPlanScreen
                  key={`ActionPlan-${actionPlanNavCounter}`}
                  onBackPress={goBack}
                  initialRoute={screenParams?.initialRoute}
                  actionPlanId={screenParams?.actionPlanId}
                />
              );
            case "Stand Up":
              return (
                <StandUpScreen
                  key={`StandUp-${standUpNavCounter}`}
                  onBackPress={goBack}
                  onHomePress={() => navigateTo("Home")}
                  initialRoute={screenParams?.initialRoute}
                  actionPlanId={screenParams?.actionPlanId}
                />
              );
            case "NHO":
              return (
                <NewHireOnboardingScreen
                  onBackPress={goBack}
                  onAdminClick={navigateToNHOAdmin}
                  onBeginOnboardingClick={navigateToNHOBegin}
                  onEmployeeProfileClick={navigateToNHOEmployeeProfile}
                />
              );
            case "NHO Admin":
              return (
                <NewHireOnboardingScreen
                  initialRoute="NHOAdmin"
                  onBackPress={goBack}
                  onHomePress={() => navigateTo("Home")}
                  onBeginOnboardingClick={navigateToNHOBegin}
                  onEmployeeProfileClick={navigateToNHOEmployeeProfile}
                />
              );
            case "NHO Manage Employees":
              return (
                <NewHireOnboardingScreen
                  initialRoute="NHOManageEmployees"
                  onBackPress={goBack}
                  onHomePress={() => navigateTo("Home")}
                  onBeginOnboardingClick={navigateToNHOBegin}
                  onEmployeeProfileClick={navigateToNHOEmployeeProfile}
                />
              );

            case "NHOEmployeeProfile":
              logger.log(
                `Rendering NHOEmployeeProfile for ID: ${selectedEmployeeId}`
              );
              return (
                <NewHireOnboardingScreen
                  initialRoute="NHOEmployeeProfile"
                  employeeId={selectedEmployeeId}
                  onBackPress={goBack}
                  onHomePress={() => navigateTo("Home")}
                  onBeginOnboardingClick={navigateToNHOBegin}
                  onEmployeeProfileClick={navigateToNHOEmployeeProfile}
                />
              );
            case "NHO Begin":
              return (
                <NewHireOnboardingScreen
                  initialRoute="NHOBegin"
                  employeeId={selectedEmployeeId}
                  onBackPress={goBack}
                  onBeginOnboardingClick={navigateToNHOBegin}
                  onEmployeeProfileClick={navigateToNHOEmployeeProfile}
                />
              );
            case "NHO PulseCheckTouchPoint":
              return (
                <PulseCheckTouchPointScreen 
                  onBackPress={goBackToDashboard}
                  onHomePress={() => navigateTo("Home")}
                />
              );
            case "NHO SpecificPeriodTouchPoint":
              return (
                <SpecificPeriodTouchPointScreen
                  onBackPress={goBackToDashboard}
                  onHomePress={() => navigateTo("Home")}
                />
              );
              case "Ticketing Portal":
                return (
                  <TicketingPortalScreen
                    onNavigate={navigateTo}
                    notificationId={screenParams?.notificationId}
                    isDeepLinked={screenParams?.isDeepLinked ?? false}
                  />
                );
              case "Submit Ticket":
                return <SubmitTicketScreen onNavigate={navigateTo} />;
              case "My Tickets":
                return <MyTicketsScreen onNavigate={navigateTo} />;
              case "Sustain Queue":
                return <SustainQueueScreen onNavigate={navigateTo} />;
              case "Ticket Detail":
                return <TicketDetailScreen onNavigate={navigateTo} ticketId={screenParams?.ticketId} isDeepLinked={screenParams?.isDeepLinked ?? false} />;
              case "Packing Slip":
                return <TicketPackingSlipScreen onNavigate={navigateTo} ticketId={screenParams?.ticketId} />;
              case "Notifications & Comms":
                return <NotificationsCommsScreen onNavigate={navigateTo} />;
              case "Team Management":
                return <SustainTeamManagementScreen onNavigate={navigateTo} />;
              case "Ticketing Configurations":
                return <TicketingConfigurationsScreen onNavigate={navigateTo} />;
              case "Spare Pool and Device Management":
                return <SparePoolScreen onNavigate={navigateTo} />;
            default:
              return <HomeScreen />;
          }
        })()}
      </Suspense>
    );
  };
 
  interface HandleSidebarClickFunction {
    (label: string): void;
  }
 
  const handleSidebarClick: HandleSidebarClickFunction = (label) => {
    setScreenStack([label]);
    setActiveTab(label);
    // Clear screen parameters when navigating via sidebar
    setScreenParams({});

    // Persist current screen (all platforms: AsyncStorage + URL hash on web)
    saveScreen(label);

    // Force remount of Stand Up screen when navigating via sidebar
    if (label === 'Stand Up') {
      setStandUpNavCounter(prev => prev + 1);
    }
  };
 
  React.useEffect(() => {
    // isMobile is computed once at mount only: phones launch in portrait and
    // should keep the hamburger/no-sidebar chrome for the whole session
    // regardless of later rotation, while tablets/iPads launch at >=768 and
    // should keep the persistent sidebar; recomputing this live would flip
    // the sidebar on/off when a phone rotates into the 768-1024 range.
    //
    // isTablet DOES need to track live width: DLOHeader's own HeaderSpacer
    // already reserves its header height in the 768-1024 range, so the
    // marginTop below must know (live) when to stay at 0 to avoid
    // double-reserving space when an iPhone rotates into landscape.
    setIsMobile(Dimensions.get('window').width < 768);

    const updateTabletRange = ({ width }: { width: number }) => {
      setIsTablet(width >= 768 && width < 1024);
    };
    updateTabletRange(Dimensions.get('window'));
    const subscription = Dimensions.addEventListener('change', ({ window }) => updateTabletRange(window));
    return () => subscription.remove();
  }, []);

  // Initialize FullStory analytics
  React.useEffect(() => {
    try {
      logger.log(`[FullStory] Initializing for platform: ${Platform.OS}`);
      
      FullStory.init({
        orgId: 'o-2454SK-na1',
        debug: true
      });
      
      logger.log('[FullStory] Initialization completed');
      
      // Track app opened event
      FullStory.event('app_opened', {
        platform: Platform.OS,
        timestamp: new Date().toISOString()
      });
      
      logger.log('[FullStory] app_opened event tracked');
    } catch (error) {
      logger.error('Failed to initialize FullStory', error);
    }
  }, []);

  // Track page views when active tab changes (skip the initial pre-hydration render)
  React.useEffect(() => {
    if (!isNavigationHydrated) return;
    const trackPageView = async () => {
      try {
        await FullStory.event('page_viewed', {
          pageName: activeTab,
        });
        logger.log('[FullStory] Page view tracked', { pageName: activeTab });
      } catch (error) {
        logger.error('Failed to track FullStory page view', error);
      }
    };
    
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    trackPageView();
  }, [activeTab, isNavigationHydrated]);

  // Block render until we know which screen to show.
  if (!isNavigationHydrated) {
    return <LHLoader />;
  }

  return (
    <EnhancedErrorBoundary componentName="App">
      <SafeAreaProvider>
        <LhToastProvider>
          <Provider store={store}>
            <ThemeProvider>
              <AppContentWrapper activeTab={activeTab} navigateTo={navigateTo} screenParams={screenParams} isMobile={isMobile} isTablet={isTablet} renderScreen={renderScreen} perfectTruckScreenRef={perfectTruckScreenRef} />
            </ThemeProvider>
          </Provider>
        </LhToastProvider>
      </SafeAreaProvider>
    </EnhancedErrorBoundary>
  );
};

// Wrapper component that uses Redux (inside Provider)
const AppContentWrapper: React.FC<{
  activeTab: string;
  navigateTo: (screen: string, params?: any) => void;
  screenParams: any;
  isMobile: boolean;
  isTablet: boolean;
  renderScreen: (peopleHubActiveSession?: any) => React.ReactNode;
  perfectTruckScreenRef: React.RefObject<PerfectTruckScreenHandle>;
}> = ({ activeTab, navigateTo, screenParams, isMobile, isTablet, renderScreen, perfectTruckScreenRef }) => {
  const toast = useToast();
  // Release DLO locks when navigating away from DLO module
  const activeLocks = useSelector((state: RootState) => state.sessionLock?.activeLocks);
  const ticketingUserInfo = useSelector((state: RootState) => state.appData?.userInfo);
  const peopleHubActiveSession = useSelector((state: RootState) => state.peopleHubActivity?.activeSession);

  const canViewReportingAnalytics = React.useMemo(() => {
    const isSustainTeamMember = ticketingUserInfo?.sustainTeamMember === true;
    const normalizedRoleName = String(ticketingUserInfo?.roleName ?? '').trim().toLowerCase();
    const isCoeRole = normalizedRoleName === 'coe' || normalizedRoleName.includes('center of excellence');
    const isSuperUser = ticketingUserInfo?.roleName === 'Super User';

    return isSustainTeamMember || isCoeRole || isSuperUser;
  }, [ticketingUserInfo?.roleName, ticketingUserInfo?.sustainTeamMember]);

  const canViewRestrictedNavigatorReports = React.useMemo(
    () => Number(ticketingUserInfo?.roleId) === 1,
    [ticketingUserInfo?.roleId]
  );

  React.useEffect(() => {
    if (activeTab !== "Ticketing Analytics Report") {
      return;
    }

    // Guard: Don't check permissions until userInfo is loaded
    // This prevents false "access denied" errors on page refresh before auth completes
    if (!ticketingUserInfo || (!ticketingUserInfo.sustainTeamMember && !ticketingUserInfo.roleName)) {
      // User info not yet loaded - wait for it before checking permissions
      return;
    }

    if (canViewReportingAnalytics) {
      return;
    }

    toast.show(
      "Access denied. You do not have permission to view Reporting and Analytics.",
      "error"
    );
    navigateTo("Home");
  }, [activeTab, canViewReportingAnalytics, navigateTo, toast, ticketingUserInfo]);

  React.useEffect(() => {
    const isRestrictedNavigatorReport =
      activeTab === "/navigator-reports/crewing-analytics"
      || activeTab === "/navigator-reports/asn-gap";

    if (!isRestrictedNavigatorReport) {
      return;
    }

    // Wait for user context before evaluating role access.
    if (!ticketingUserInfo || (!ticketingUserInfo.roleId && !ticketingUserInfo.roleName)) {
      return;
    }

    if (canViewRestrictedNavigatorReports) {
      return;
    }

    toast.show(
      "Access denied. Only Super User (Role ID 1) can view this report.",
      "error"
    );
    navigateTo("Home");
  }, [activeTab, canViewRestrictedNavigatorReports, navigateTo, ticketingUserInfo, toast]);

  React.useEffect(() => {
    // Only release locks if we're leaving DLO module
    if (!activeTab.startsWith("DLO") && !activeTab.startsWith("Labor Optimization")) {
      if (activeLocks && Object.keys(activeLocks).length > 0) {
        const releaseLocks = async () => {
          try {
            const { getSessionLockService } = await import("./src/api/sessionLockService");
            const lockService = getSessionLockService();
            
            if (lockService) {
              console.log('[App] Releasing DLO locks on module navigation away:', Object.keys(activeLocks));
              for (const [resourceId, lockData] of Object.entries(activeLocks)) {
                try {
                  await lockService.releaseLock(resourceId, (lockData as any).lockToken);
                } catch (error) {
                  console.error('[App] Error releasing lock:', error);
                }
              }
            }
          } catch (error) {
            console.error('[App] Failed to release locks:', error);
          }
        };
        
        releaseLocks();
      }
    }
  }, [activeTab, activeLocks]);

  const dispatch = useDispatch();

  // Handle deep-linked tickets by setting the ticketDetailSource to allow access
  React.useEffect(() => {
    if (screenParams?.isDeepLinked && activeTab === "Ticket Detail") {
      dispatch(setTicketDetailSource("Submit Ticket"));
      if (screenParams?.ticketId) {
        dispatch(setSelectedTicketId(screenParams.ticketId));
      }
    }
  }, [screenParams?.isDeepLinked, screenParams?.ticketId, activeTab, dispatch]);

  return (
    <LocationInitializer>
              <MsalProvider>
                <AppWrapper>
                  {/* Check if current screen is DLO - render without main layout (only on web) */}
                  {(activeTab.startsWith("DLO") || activeTab.startsWith("Labor Optimization")) && Platform.OS !== 'ios' ? (
                    <DLOApp activeScreen={activeTab} screenParams={screenParams} onNavigate={navigateTo} />
                  ) : (
                    <View testID="app-root" style={styles.container}>
                    <DLOHeader activeScreen={activeTab} onNavigate={(screen, params) => {
                      if (activeTab === 'Perfect Truck' && screen !== 'Perfect Truck' && perfectTruckScreenRef.current) {
                        perfectTruckScreenRef.current.handleNavigationAttempt(() => navigateTo(screen, params));
                      } else {
                        navigateTo(screen, params);
                      }
                    }} />
                    {/* DLOHeader already reserves insets.top (its own status-bar spacer) and,
                        in the 768-1024 landscape/tablet width range, its own header height via
                        HeaderSpacer - so marginTop only needs to add the remaining 118px for the
                        plain portrait-phone case, and must stay 0 in the tablet-width range to
                        avoid double-reserving space (large blank gap under the header). Body
                        content itself has no edge-hugging controls, so it can use the full
                        width - only the header (buttons near the edge) needs the notch padding. */}
                    <View style={{
                      flex: 1,
                      marginTop: isMobile && !isTablet ? 118 : 0,
                    }}>
                      <AnnouncementBanner />
                      <View style={styles.body}>
                        {/* Sidebar */}
                        <DLOSidebar
                          activeScreen={activeTab}
                          onNavigate={(screen) => {
                            if (activeTab === 'Perfect Truck' && screen !== 'Perfect Truck' && perfectTruckScreenRef.current) {
                              perfectTruckScreenRef.current.handleNavigationAttempt(() => navigateTo(screen));
                            } else {
                              navigateTo(screen);
                            }
                          }}
                          isVisible={!isMobile}
                        />

                        <View style={styles.content}>{renderScreen(peopleHubActiveSession)}</View>
                      </View>
                    </View>

                    {/* <CounterComponent /> */}
                  </View>
                  )}
                </AppWrapper>
              </MsalProvider>
            </LocationInitializer>
          );
};

const styles = StyleSheet.create({
  container: {
    height: "100%",
    width: "100%",
    flex: 1,
    padding: 0,
    margin: 0,
  },
  body: {
    flex: 1,
    flexDirection: "row",
  },
  content: {
    flex: 1,
    backgroundColor: "#f8f8f8",
  },
});

export default App;

